import React from "react";
import { Autocomplete, AutocompleteRenderInputParams, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { IdNamePair } from "../models";

interface IAutocompleteSearchProps {
    searchableItems: IdNamePair<string, string>[];
    currentItem: IdNamePair<string, string>;
    onChange: (item: IdNamePair<string, string> | null) => void;
    itemListHeight?: number;
    maxItemsVisible?: number;
    placeholderText?: string;
    isLoading?: boolean;
    isLoadingText?: React.ReactNode;
}

const AutocompleteSearch: React.FC<IAutocompleteSearchProps> = ({
    searchableItems,
    currentItem,
    onChange,
    itemListHeight,
    maxItemsVisible,
    placeholderText,
    isLoading,
    isLoadingText,
}) => {
    const LIST_ITEM_HEIGHT = itemListHeight ?? 36;
    const ITEMS_VISIBLE = maxItemsVisible ?? 5;

    const searchIcon = (icon : React.ReactNode) : JSX.Element => 
        <React.Fragment>
            <InputAdornment position="start">
                <SearchIcon />
            </InputAdornment>
            {icon}
        </React.Fragment>;

    const renderTextInput = (params : AutocompleteRenderInputParams) : JSX.Element => 
        <TextField 
            {...params}
            placeholder={placeholderText}
            InputProps={{
                ...params.InputProps,
                startAdornment: searchIcon(params.InputProps.startAdornment)
            }}
            size="small"
            multiline={false}
        />;

    return <Autocomplete
        fullWidth
        selectOnFocus
        clearOnBlur
        forcePopupIcon={false}
        noOptionsText={<i>No matches found</i>}
        options={searchableItems}
        value={currentItem}
        onChange={(_, item) => onChange(item)}
        ListboxProps={{ style: { maxHeight: LIST_ITEM_HEIGHT * ITEMS_VISIBLE } }}
        isOptionEqualToValue={(option, value) => option.id === value.id || value.id === ""}
        renderInput={(params : AutocompleteRenderInputParams) => renderTextInput(params)}
        getOptionLabel={option => option.name}
        renderOption={(props, option) => 
            <li {...props} key={option.id}>
                {option.name}
            </li>
        }
        loading={isLoading}
        loadingText={isLoadingText}
    />;
};

export default AutocompleteSearch;
